import {
  Chess,
  DEFAULT_POSITION,
  type Move as ChessMove,
} from 'chess.js';

/** A promotion is always supplied explicitly; CapyChess never auto-queens. */
export type PromotionPiece = 'q' | 'r' | 'b' | 'n';

export type RulesColor = 'w' | 'b';
export type RulesPiece = 'p' | 'n' | 'b' | 'r' | 'q' | 'k';
export type UciMove = string;

export interface MoveIntent {
  /** The snapshot revision from which the interaction was made. */
  expectedRevision: number;
  from: string;
  to: string;
  promotion?: PromotionPiece;
}

/**
 * A CapyChess value copy of chess.js's documented verbose move descriptor.
 * No chess.js instance or Move object crosses this boundary.
 */
export interface VerboseLegalMove {
  uci: UciMove;
  color: RulesColor;
  from: string;
  to: string;
  piece: RulesPiece;
  captured?: RulesPiece;
  promotion?: PromotionPiece;
  flags: string;
  san: string;
  lan: string;
  before: string;
  after: string;
  isCapture: boolean;
  isPromotion: boolean;
  isEnPassant: boolean;
  isKingsideCastle: boolean;
  isQueensideCastle: boolean;
  isCastle: boolean;
  isBigPawn: boolean;
}

export type CanonicalResult =
  | { terminal: false; token: '*'; reason: 'ongoing' }
  | {
      terminal: true;
      token: '1-0' | '0-1';
      reason: 'checkmate';
      winner: RulesColor;
    }
  | {
      terminal: true;
      token: '1/2-1/2';
      reason:
        | 'stalemate'
        | 'threefold-repetition'
        | 'fifty-move-rule'
        | 'insufficient-material'
        | 'draw';
    };

export interface RulesSnapshot {
  /** Monotonic identity for the current authoritative state. */
  revision: number;
  /** The normalized, six-field FEN from which accepted events are replayed. */
  initialFen: string;
  /** Current normalized, six-field FEN. */
  fen: string;
  turn: RulesColor;
  /** The side to move when chess.js has authoritatively determined it is in check. */
  checkedColor?: RulesColor;
  /** Ordered legal coordinate moves, including each separate promotion choice. */
  legalUciMoves: readonly UciMove[];
  /** Ordered verbose legal moves supplied as CapyChess value data. */
  legalMoves: readonly VerboseLegalMove[];
  /** Ordered accepted coordinate moves, suitable for persistence/replay. */
  uciHistory: readonly UciMove[];
  /** Ordered SAN move history for presentation. */
  sanHistory: readonly string[];
  /**
   * Count of non-king pieces still owned by each side. This value-only rules
   * semantic lets session adjudication prove the narrow bare-king timeout
   * case without exposing or reconstructing the backing chess.js board.
   */
  nonKingPieceCount: Readonly<Record<RulesColor, number>>;
  result: CanonicalResult;
}

/** The only persistence shape accepted by this rules adapter. */
export interface RulesReplay {
  initialFen: string;
  acceptedUciEvents: readonly UciMove[];
}

export interface AcceptedMove {
  uci: UciMove;
  san: string;
  verbose: VerboseLegalMove;
}

export type RulesRejectionReason =
  | 'invalid-intent'
  | 'stale-revision'
  | 'terminal'
  | 'promotion-required'
  | 'illegal-move'
  | 'no-moves-to-undo'
  | 'invalid-initial-fen'
  | 'invalid-replay-event'
  | 'illegal-replay-event';

export type MoveAttempt<T> =
  | { accepted: true; value: T; snapshot: RulesSnapshot }
  | {
      accepted: false;
      reason: RulesRejectionReason;
      snapshot: RulesSnapshot;
    };

/** The renderer-neutral contract consumed by CapyChess adapters. */
export interface RulesPort {
  snapshot(): RulesSnapshot;
  submit(intent: MoveIntent): MoveAttempt<AcceptedMove>;
  undo(expectedRevision?: number): MoveAttempt<AcceptedMove>;
  reset(initialFen?: string): MoveAttempt<RulesSnapshot>;
  load(record: RulesReplay): MoveAttempt<RulesSnapshot>;
  replay(record: RulesReplay): MoveAttempt<RulesSnapshot>;
  exportReplay(): RulesReplay;
}

const SQUARE_PATTERN = /^[a-h][1-8]$/;
const UCI_PATTERN = /^([a-h][1-8])([a-h][1-8])([qrbn])?$/;

function isPromotionPiece(value: unknown): value is PromotionPiece {
  return value === 'q' || value === 'r' || value === 'b' || value === 'n';
}

function isSquare(value: unknown): value is string {
  return typeof value === 'string' && SQUARE_PATTERN.test(value);
}

function toUci(move: Pick<ChessMove, 'from' | 'to' | 'promotion'>): UciMove {
  return `${move.from}${move.to}${move.promotion ?? ''}`;
}

function toVerboseMove(move: ChessMove): VerboseLegalMove {
  const isKingsideCastle = move.isKingsideCastle();
  const isQueensideCastle = move.isQueensideCastle();
  const verbose: VerboseLegalMove = {
    uci: toUci(move),
    color: move.color,
    from: move.from,
    to: move.to,
    piece: move.piece,
    flags: move.flags,
    san: move.san,
    lan: move.lan,
    before: move.before,
    after: move.after,
    isCapture: move.isCapture(),
    isPromotion: move.isPromotion(),
    isEnPassant: move.isEnPassant(),
    isKingsideCastle,
    isQueensideCastle,
    isCastle: isKingsideCastle || isQueensideCastle,
    isBigPawn: move.isBigPawn(),
  };

  if (move.captured !== undefined) {
    verbose.captured = move.captured;
  }

  if (move.promotion !== undefined) {
    verbose.promotion = move.promotion as PromotionPiece;
  }

  return Object.freeze(verbose);
}

function toResult(chess: Chess): CanonicalResult {
  if (!chess.isGameOver()) {
    return Object.freeze({ terminal: false, token: '*', reason: 'ongoing' });
  }

  if (chess.isCheckmate()) {
    return Object.freeze({
      terminal: true,
      token: chess.turn() === 'w' ? '0-1' : '1-0',
      reason: 'checkmate',
      winner: chess.turn() === 'w' ? 'b' : 'w',
    });
  }

  // Preserve the pinned chess.js 1.4.0 draw precedence when exposing a reason.
  if (chess.isDrawByFiftyMoves()) {
    return Object.freeze({
      terminal: true,
      token: '1/2-1/2',
      reason: 'fifty-move-rule',
    });
  }

  if (chess.isStalemate()) {
    return Object.freeze({ terminal: true, token: '1/2-1/2', reason: 'stalemate' });
  }

  if (chess.isInsufficientMaterial()) {
    return Object.freeze({
      terminal: true,
      token: '1/2-1/2',
      reason: 'insufficient-material',
    });
  }

  if (chess.isThreefoldRepetition()) {
    return Object.freeze({
      terminal: true,
      token: '1/2-1/2',
      reason: 'threefold-repetition',
    });
  }

  // isGameOver() is true only for checkmate or isDraw() in chess.js 1.4.0.
  return Object.freeze({ terminal: true, token: '1/2-1/2', reason: 'draw' });
}

interface RebuiltGame {
  chess: Chess;
  initialFen: string;
  uciHistory: UciMove[];
}

/**
 * The sole CapyChess move-commit authority. The backing chess.js object never
 * leaves this module, so UI, AI, persistence, and board adapters can exchange
 * only snapshots and move intents.
 */
export class ChessJsRulesPort implements RulesPort {
  private chess: Chess;
  private initialFen: string;
  private uciHistory: UciMove[] = [];
  private revision = 0;

  public constructor(initialFen = DEFAULT_POSITION) {
    this.chess = new Chess(initialFen);
    this.initialFen = this.chess.fen();
  }

  /** Returns an immutable value projection of the current authoritative game. */
  public snapshot(): RulesSnapshot {
    const result = toResult(this.chess);
    const legalMoves = result.terminal
      ? []
      : this.chess.moves({ verbose: true }).map(toVerboseMove);
    const checkedColor = this.chess.isCheck() ? this.chess.turn() : undefined;
    const nonKingPieceCount: Record<RulesColor, number> = { w: 0, b: 0 };
    for (const rank of this.chess.board()) {
      for (const piece of rank) {
        if (piece !== null && piece.type !== 'k') {
          nonKingPieceCount[piece.color] += 1;
        }
      }
    }

    return Object.freeze({
      revision: this.revision,
      initialFen: this.initialFen,
      fen: this.chess.fen(),
      turn: this.chess.turn(),
      ...(checkedColor === undefined ? {} : { checkedColor }),
      legalUciMoves: Object.freeze(legalMoves.map((move) => move.uci)),
      legalMoves: Object.freeze(legalMoves),
      uciHistory: Object.freeze([...this.uciHistory]),
      sanHistory: Object.freeze([...this.chess.history()]),
      nonKingPieceCount: Object.freeze(nonKingPieceCount),
      result,
    });
  }

  public submit(intent: MoveIntent): MoveAttempt<AcceptedMove> {
    if (
      intent === null ||
      typeof intent !== 'object' ||
      !Number.isSafeInteger(intent.expectedRevision) ||
      !isSquare(intent.from) ||
      !isSquare(intent.to) ||
      (intent.promotion !== undefined && !isPromotionPiece(intent.promotion))
    ) {
      return this.reject('invalid-intent');
    }

    if (intent.expectedRevision !== this.revision) {
      return this.reject('stale-revision');
    }

    if (this.chess.isGameOver()) {
      return this.reject('terminal');
    }

    const candidates = this.chess
      .moves({ verbose: true })
      .filter((move) => move.from === intent.from && move.to === intent.to);

    if (candidates.length === 0) {
      return this.reject('illegal-move');
    }

    if (candidates.some((move) => move.promotion !== undefined) && intent.promotion === undefined) {
      return this.reject('promotion-required');
    }

    const selected = candidates.find(
      (move) => move.promotion === intent.promotion,
    );

    if (selected === undefined) {
      return this.reject('illegal-move');
    }

    let committed: ChessMove;
    try {
      committed = this.chess.move({
        from: selected.from,
        to: selected.to,
        promotion: selected.promotion,
      });
    } catch {
      // chess.js remains the commit authority; failed commits do not advance state.
      return this.reject('illegal-move');
    }

    const verbose = toVerboseMove(committed);
    this.uciHistory.push(verbose.uci);
    this.revision += 1;

    return this.accept({ uci: verbose.uci, san: verbose.san, verbose });
  }

  /** Undoes exactly one accepted half-move, with an optional stale-state guard. */
  public undo(expectedRevision = this.revision): MoveAttempt<AcceptedMove> {
    if (!Number.isSafeInteger(expectedRevision)) {
      return this.reject('invalid-intent');
    }

    if (expectedRevision !== this.revision) {
      return this.reject('stale-revision');
    }

    const undone = this.chess.undo();
    if (undone === null) {
      return this.reject('no-moves-to-undo');
    }

    const verbose = toVerboseMove(undone);
    this.uciHistory.pop();
    this.revision += 1;

    return this.accept({ uci: verbose.uci, san: verbose.san, verbose });
  }

  /** Starts a clean game from a normalized initial FEN. */
  public reset(initialFen = DEFAULT_POSITION): MoveAttempt<RulesSnapshot> {
    return this.replaceFromReplay({ initialFen, acceptedUciEvents: [] });
  }

  /** Restores only a replayable initial-FEN-plus-events record. */
  public load(record: RulesReplay): MoveAttempt<RulesSnapshot> {
    return this.replaceFromReplay(record);
  }

  /** Alias that makes persistence reconstruction explicit at the call site. */
  public replay(record: RulesReplay): MoveAttempt<RulesSnapshot> {
    return this.replaceFromReplay(record);
  }

  /** Produces the complete replay input; current FEN alone is deliberately absent. */
  public exportReplay(): RulesReplay {
    return Object.freeze({
      initialFen: this.initialFen,
      acceptedUciEvents: Object.freeze([...this.uciHistory]),
    });
  }

  private replaceFromReplay(record: RulesReplay): MoveAttempt<RulesSnapshot> {
    const rebuilt = this.rebuild(record);
    if ('reason' in rebuilt) {
      return this.reject(rebuilt.reason);
    }

    this.chess = rebuilt.chess;
    this.initialFen = rebuilt.initialFen;
    this.uciHistory = rebuilt.uciHistory;
    this.revision += 1;

    return this.accept(this.snapshot());
  }

  private rebuild(
    record: RulesReplay,
  ): RebuiltGame | { reason: RulesRejectionReason } {
    if (
      record === null ||
      typeof record !== 'object' ||
      typeof record.initialFen !== 'string' ||
      !Array.isArray(record.acceptedUciEvents)
    ) {
      return { reason: 'invalid-replay-event' };
    }

    let chess: Chess;
    try {
      chess = new Chess(record.initialFen);
    } catch {
      return { reason: 'invalid-initial-fen' };
    }

    const initialFen = chess.fen();
    const uciHistory: UciMove[] = [];

    for (const rawEvent of record.acceptedUciEvents) {
      if (typeof rawEvent !== 'string') {
        return { reason: 'invalid-replay-event' };
      }

      const parsed = UCI_PATTERN.exec(rawEvent);
      if (parsed === null) {
        return { reason: 'invalid-replay-event' };
      }

      if (chess.isGameOver()) {
        return { reason: 'illegal-replay-event' };
      }

      const [, from, to, promotion] = parsed;
      const candidates = chess
        .moves({ verbose: true })
        .filter((move) => move.from === from && move.to === to);

      if (candidates.length === 0) {
        return { reason: 'illegal-replay-event' };
      }

      if (candidates.some((move) => move.promotion !== undefined) && promotion === undefined) {
        return { reason: 'illegal-replay-event' };
      }

      const selected = candidates.find((move) => move.promotion === promotion);
      if (selected === undefined) {
        return { reason: 'illegal-replay-event' };
      }

      try {
        const committed = chess.move({
          from: selected.from,
          to: selected.to,
          promotion: selected.promotion,
        });
        uciHistory.push(toUci(committed));
      } catch {
        return { reason: 'illegal-replay-event' };
      }
    }

    return { chess, initialFen, uciHistory };
  }

  private accept<T>(value: T): MoveAttempt<T> {
    return { accepted: true, value, snapshot: this.snapshot() };
  }

  private reject(reason: RulesRejectionReason): MoveAttempt<never> {
    return { accepted: false, reason, snapshot: this.snapshot() };
  }
}
